/**
 * Accounts receivable (KAN-38 / KAN-55).
 *
 * Everything on this screen is computed from the invoices as they stand, so
 * there is no "generated at" caveat of the kind the forecast carries — the
 * figures and the invoices behind them are the same read. What the screen does
 * have to say out loud is the *rule*: which accounts are flagged depends on two
 * thresholds, and those thresholds are shown as editable controls rather than
 * buried in a config file, because "high risk" is a claim somebody has to be
 * able to check.
 *
 * The aging split is stated in full rather than reduced to "overdue", and the
 * two buckets outside KAN-55's list — not yet due, and no due date recorded —
 * are shown where they cannot be mistaken for late money. Both are the
 * difference between a receivable and a problem.
 */
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AGING_BUCKETS,
  AGING_BUCKET_META,
  AR_GROUPINGS,
  AR_GROUPING_META,
  AR_RISK_META,
  AR_SORTS,
  AR_SORT_META,
  DEFAULT_AR_RISK_RULE,
  FUNDING_STATUS_META,
  PAYMENT_COMMITMENT_STATUS_LABELS,
  fromDecimalString,
  type AgingBucket,
  type ArGrouping,
  type ArInvoiceDetail,
  type ArLine,
  type ArReconciliation,
  type ArRiskLevel,
  type ArSort,
  type ArSummary,
  type FundingStatus,
  type PaymentCommitmentStatus,
} from '@accounting/shared'
import { ApiError, apiDownload, apiFetch } from '../lib/api'
import { useSession } from '../lib/session'
import { formatCount, formatDate, formatDateTime, formatMoney, formatRelative } from '../lib/format'
import { Alert } from '../ui/Alert'
import { Badge, type Tone } from '../ui/Badge'
import { Button } from '../ui/Button'
import { Card, CardBody, CardHeader } from '../ui/Card'
import { Field, Select, TextInput } from '../ui/Field'
import { PageHeader } from '../ui/PageHeader'
import { Stat, StatRow } from '../ui/Stat'
import { EmptyRow, TableWrap, Td, Th } from '../ui/Table'

interface ArReport {
  summary: ArSummary
  lines: ArLine[]
}

/** One colour per risk level, used wherever a level is named. */
const RISK_TONES: Record<ArRiskLevel, Tone> = {
  high: 'critical',
  watch: 'warning',
  none: 'neutral',
}

/**
 * Funded reads as positive and unfunded as neutral rather than critical.
 *
 * Unfunded is the *normal* state of a receivable — most bills are not prepaid —
 * so colouring it as a problem would put a red badge on nearly every row and
 * leave the genuine signal, the risk level, competing with it.
 */
const FUNDING_TONES: Record<FundingStatus, Tone> = {
  funded: 'positive',
  partially_funded: 'brand',
  unfunded: 'neutral',
}

/** The bar segments, in aging order. Colder to hotter as money gets later. */
const BUCKET_FILLS: Record<AgingBucket, string> = {
  not_yet_due: 'bg-brand-300',
  days_0_30: 'bg-brand-600',
  days_31_60: 'bg-accent-400',
  days_61_90: 'bg-accent-600',
  days_90_plus: 'bg-red-600',
  undated: 'bg-ink-400',
}

export function AccountsReceivable() {
  const { may } = useSession()
  const [grouping, setGrouping] = useState<ArGrouping>('client')
  const [sort, setSort] = useState<ArSort>('risk')
  const [minDays, setMinDays] = useState(String(DEFAULT_AR_RISK_RULE.minDaysOverdue))
  /** Held as dollars because that is what a person types; sent as minor units. */
  const [minBalance, setMinBalance] = useState(
    String(DEFAULT_AR_RISK_RULE.minBalanceMinor / 100)
  )
  const [expanded, setExpanded] = useState<string | null>(null)

  const rule = useMemo(() => riskRuleFrom(minDays, minBalance), [minDays, minBalance])

  const query = new URLSearchParams({
    groupBy: grouping,
    sort,
    riskDaysOverdue: String(rule.minDaysOverdue),
    riskBalanceMinor: String(rule.minBalanceMinor),
  })

  const report = useQuery({
    queryKey: ['ar', grouping, sort, rule.minDaysOverdue, rule.minBalanceMinor],
    queryFn: () => apiFetch<ArReport>(`/ar?${query.toString()}`),
  })

  const summary = report.data?.summary ?? null
  const lines = report.data?.lines ?? []

  /** The row opened is addressed by group, and no group survives a regrouping. */
  function changeGrouping(next: ArGrouping) {
    setGrouping(next)
    setExpanded(null)
  }

  return (
    <>
      <PageHeader
        title="Accounts receivable"
        description="What the firm is owed, how late it is, how much of it is already funded from trust, and which accounts the risk rule flags for collection."
        actions={<ExportButton query={query} />}
      />

      {report.error && (
        <Alert tone="error" className="mb-4">
          {messageFor(report.error)}
        </Alert>
      )}

      <Card className="mb-6">
        <CardBody>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Field
              id="ar-group"
              label="Group by"
              hint={AR_GROUPING_META[grouping].description}
            >
              <Select
                id="ar-group"
                value={grouping}
                onChange={(event) => changeGrouping(event.target.value as ArGrouping)}
              >
                {AR_GROUPINGS.map((option) => (
                  <option key={option} value={option}>
                    {AR_GROUPING_META[option].label}
                  </option>
                ))}
              </Select>
            </Field>

            <Field id="ar-sort" label="Order" hint={AR_SORT_META[sort].description}>
              <Select
                id="ar-sort"
                value={sort}
                onChange={(event) => setSort(event.target.value as ArSort)}
              >
                {AR_SORTS.map((option) => (
                  <option key={option} value={option}>
                    {AR_SORT_META[option].label}
                  </option>
                ))}
              </Select>
            </Field>

            {/*
              * The risk rule, as two controls rather than a saved setting. Both
              * halves have to be met for an account to be flagged, which is
              * said here because a threshold shown without the conjunction
              * reads as either-or.
              */}
            <Field
              id="ar-risk-days"
              label="Flag after"
              hint="Whole days past due on the oldest overdue invoice."
            >
              <TextInput
                id="ar-risk-days"
                type="number"
                min={0}
                step={5}
                inputMode="numeric"
                value={minDays}
                onChange={(event) => setMinDays(event.target.value)}
              />
            </Field>

            <Field
              id="ar-risk-balance"
              label="…and overdue above"
              hint="US dollars. Measured on the overdue balance, not everything outstanding."
            >
              <TextInput
                id="ar-risk-balance"
                type="number"
                min={0}
                step={500}
                inputMode="decimal"
                value={minBalance}
                onChange={(event) => setMinBalance(event.target.value)}
              />
            </Field>
          </div>

          {!isDefaultRule(rule) && (
            <div className="mt-4">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setMinDays(String(DEFAULT_AR_RISK_RULE.minDaysOverdue))
                  setMinBalance(String(DEFAULT_AR_RISK_RULE.minBalanceMinor / 100))
                }}
              >
                Back to the firm default
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      {report.isPending && <p className="text-sm text-ink-600">Loading receivables…</p>}

      {summary && summary.invoiceCount === 0 && (
        <Alert tone="info" title="Nothing is outstanding">
          No issued invoice carries a balance. Until Clio is connected there is nothing to report
          from —{' '}
          <Link to="/integrations" className="underline">
            integration status
          </Link>{' '}
          shows what is waiting on credentials.
        </Alert>
      )}

      {summary && summary.invoiceCount > 0 && (
        <>
          <StatRow className="mb-3">
            <Stat
              label="Outstanding"
              value={formatMoney(summary.outstandingMinor)}
              hint={`${formatCount(summary.invoiceCount)} open ${
                summary.invoiceCount === 1 ? 'invoice' : 'invoices'
              } across ${formatCount(summary.clientCount)} ${
                summary.clientCount === 1 ? 'client' : 'clients'
              }.`}
            />
            <Stat
              label="Overdue"
              value={formatMoney(summary.overdueMinor)}
              tone={summary.overdueMinor > 0 ? 'warning' : 'muted'}
              hint="Past its due date. Excludes anything not yet due and anything with no due date recorded."
            />
            <Stat
              label="High risk"
              value={formatMoney(summary.highRiskOverdueMinor)}
              tone={summary.highRiskCount > 0 ? 'warning' : 'muted'}
              hint={
                summary.highRiskCount > 0
                  ? `${summary.highRiskCount} ${
                      summary.highRiskCount === 1 ? 'account' : 'accounts'
                    } at least ${summary.rule.minDaysOverdue} days late owing over ${formatMoney(
                      summary.rule.minBalanceMinor
                    )}.`
                  : 'No account meets both halves of the rule above.'
              }
            />
            <Stat
              label="Trust held"
              value={formatMoney(summary.trustAvailableMinor)}
              tone="muted"
              hint="Against the clients carrying a balance. Some of the AR beside it is a transfer to post rather than a collection."
            />
          </StatRow>

          <Card className="mb-6">
            <CardHeader
              title="Aging"
              description="Every outstanding balance, by days past its due date."
            />
            <CardBody>
              <AgingBar buckets={summary.buckets} total={summary.outstandingMinor} />

              <dl className="mt-5 grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
                {AGING_BUCKETS.filter(
                  /**
                   * `undated` is hidden when it is empty — which is the normal
                   * case — because a bucket that exists to surface a data gap
                   * should not be a permanent row of zeros. Every other bucket
                   * stays visible at zero: "nothing is 90+ days late" is
                   * information, and a missing row is not.
                   */
                  (bucket) => bucket !== 'undated' || summary.buckets.undated !== 0
                ).map((bucket) => (
                  <div key={bucket} className="flex gap-3">
                    <span
                      aria-hidden="true"
                      className={`mt-1 h-3 w-1 shrink-0 rounded-full ${BUCKET_FILLS[bucket]}`}
                    />
                    <div className="min-w-0">
                      <dt className="text-[11px] font-semibold tracking-[0.12em] text-ink-500 uppercase">
                        {AGING_BUCKET_META[bucket].label}
                      </dt>
                      <dd className="font-display text-lg font-semibold tabular-nums text-brand-900">
                        {formatMoney(summary.buckets[bucket])}
                      </dd>
                      <dd className="mt-0.5 text-xs leading-snug text-ink-600">
                        {AGING_BUCKET_META[bucket].description}
                      </dd>
                    </div>
                  </div>
                ))}
              </dl>
            </CardBody>
          </Card>

          <Reconciliation />

          <Card>
            <CardHeader
              title={`By ${AR_GROUPING_META[grouping].label.toLowerCase()}`}
              description={`${AR_SORT_META[sort].label}. Expand a row for the invoices behind it and what has been paid against them.`}
              actions={<Badge tone="neutral">{lines.length}</Badge>}
            />

            <TableWrap>
              <thead>
                <tr>
                  <Th>{AR_GROUPING_META[grouping].label}</Th>
                  <Th className="text-right">Outstanding</Th>
                  <Th className="text-right">Overdue</Th>
                  <Th className="text-right">Oldest</Th>
                  <Th>Funding</Th>
                  <Th>Last paid</Th>
                  <Th>Promised</Th>
                  <Th>Risk</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 && (
                  <EmptyRow colSpan={9}>Nothing outstanding under this grouping.</EmptyRow>
                )}

                {lines.map((line) => (
                  <LineRow
                    key={line.groupId ?? '__unattributed__'}
                    line={line}
                    grouping={grouping}
                    expanded={expanded === (line.groupId ?? '__unattributed__')}
                    onToggle={() =>
                      setExpanded(
                        expanded === (line.groupId ?? '__unattributed__')
                          ? null
                          : (line.groupId ?? '__unattributed__')
                      )
                    }
                  />
                ))}
              </tbody>
            </TableWrap>
          </Card>

          <Commitments canManage={may('ar.manage')} />

          <p className="mt-6 max-w-3xl text-xs leading-relaxed text-ink-600">
            Aging counts whole calendar days in the firm's timezone, so a balance moves bucket at
            midnight in New York rather than at whatever hour this page was opened. Trust is read as
            it stands today, never as of a past date — a balance that has since paid another bill
            cannot fund this one. Draft, void and written-off invoices are not receivables: a
            write-off is a decision not to collect, and it is already accounted for in the billing
            forecast's realization rate.
          </p>
        </>
      )}
    </>
  )
}

/**
 * The aging split as one bar.
 *
 * Proportional widths only — no axis and no percentages printed on the
 * segments. The figures underneath are the numbers; this is for seeing at a
 * glance whether the balance is weighted towards the recent end or the old one,
 * which a column of currency values genuinely does not show.
 */
function AgingBar({
  buckets,
  total,
}: {
  buckets: Record<AgingBucket, number>
  total: number
}) {
  if (total <= 0) return null

  return (
    <div className="flex h-3 w-full overflow-hidden rounded-full bg-ink-100">
      {AGING_BUCKETS.map((bucket) => {
        const share = (buckets[bucket] / total) * 100
        if (share <= 0) return null

        return (
          <div
            key={bucket}
            className={BUCKET_FILLS[bucket]}
            style={{ width: `${share}%` }}
            title={`${AGING_BUCKET_META[bucket].label}: ${formatMoney(buckets[bucket])}`}
          />
        )
      })}
    </div>
  )
}

function LineRow({
  line,
  grouping,
  expanded,
  onToggle,
}: {
  line: ArLine
  grouping: ArGrouping
  expanded: boolean
  onToggle: () => void
}) {
  return (
    <>
      <tr className={expanded ? 'bg-brand-50/50' : undefined}>
        <Td>
          <span className="block text-ink-900">{line.groupLabel}</span>
          <span className="block text-xs text-ink-600">
            {subtitleFor(line, grouping)}
            {line.invoiceCount > 0 && (
              <>
                {subtitleFor(line, grouping) && ' · '}
                {line.invoiceCount} {line.invoiceCount === 1 ? 'invoice' : 'invoices'}
              </>
            )}
          </span>
        </Td>
        <Td className="text-right tabular-nums">{formatMoney(line.outstandingMinor)}</Td>
        <Td className="text-right tabular-nums">
          {line.overdueMinor > 0 ? (
            formatMoney(line.overdueMinor)
          ) : (
            <span className="text-ink-500">—</span>
          )}
        </Td>
        <Td className="text-right tabular-nums">
          {line.maxDaysOverdue > 0 ? (
            `${formatCount(line.maxDaysOverdue)} d`
          ) : (
            <span className="text-ink-500">—</span>
          )}
        </Td>
        <Td>
          <span title={FUNDING_STATUS_META[line.fundingStatus].description}>
            <Badge tone={FUNDING_TONES[line.fundingStatus]}>
              {FUNDING_STATUS_META[line.fundingStatus].label}
            </Badge>
          </span>
          {line.trustAvailableMinor > 0 && (
            <span className="mt-1 block text-xs tabular-nums text-ink-600">
              {formatMoney(line.trustAvailableMinor)} in trust
            </span>
          )}
        </Td>
        <Td>
          {line.lastPaymentAt ? (
            <>
              <span className="block text-ink-800">{formatRelative(line.lastPaymentAt)}</span>
              <span className="block text-xs tabular-nums text-ink-600">
                {formatMoney(line.lastPaymentMinor)}
                {/*
                  * Stated separately from the last receipt, because "paid
                  * something last week" and "has paid nothing all quarter" are
                  * different conversations and the risk rule cannot tell them
                  * apart on its own.
                  */}
                {line.recentPaymentsMinor !== line.lastPaymentMinor && (
                  <> · {formatMoney(line.recentPaymentsMinor)} in 90 days</>
                )}
              </span>
            </>
          ) : (
            <span className="text-xs text-ink-500">Never paid</span>
          )}
        </Td>
        {/*
          * Promised, never netted. A commitment is evidence about a
          * conversation rather than about money, and a column that reduced the
          * balance by what somebody said would arrive would be the most
          * misleading thing on this screen.
          */}
        <Td>
          {line.committedMinor === 0 ? (
            <span className="text-xs text-ink-500">Not chased</span>
          ) : (
            <>
              <span className="block tabular-nums text-ink-800">
                {formatMoney(line.committedMinor)}
              </span>
              <span className="block text-xs text-ink-600">
                by {formatDate(line.nextCommitmentOn)}
                {line.overdueCommitmentCount > 0 && (
                  <span className="ml-1 text-accent-700">
                    · {line.overdueCommitmentCount} past due
                  </span>
                )}
              </span>
            </>
          )}
        </Td>
        <Td>
          <span title={AR_RISK_META[line.riskLevel].description}>
            <Badge tone={RISK_TONES[line.riskLevel]}>{AR_RISK_META[line.riskLevel].label}</Badge>
          </span>
        </Td>
        <Td className="text-right">
          <Button size="sm" variant="ghost" onClick={onToggle} aria-expanded={expanded}>
            {expanded ? 'Hide' : 'Invoices'}
          </Button>
        </Td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={8} className="border-b border-ink-200 bg-ink-50 px-5 py-4">
            <LineInvoices line={line} grouping={grouping} />
          </td>
        </tr>
      )}
    </>
  )
}

/**
 * The drill-down: the open invoices behind one line, each with the receipts
 * applied to it. Loaded on expand rather than with the page — a firm-wide AR
 * report would otherwise fetch every open invoice to show four.
 */
function LineInvoices({ line, grouping }: { line: ArLine; grouping: ArGrouping }) {
  const query = new URLSearchParams({ groupBy: grouping })
  /**
   * Omitted, not blanked, for the unattributed row. An empty `groupId` and an
   * absent one mean the same thing to the endpoint, and sending neither is what
   * that row is: invoices with no matter, or matters with nobody responsible.
   */
  if (line.groupId) query.set('groupId', line.groupId)

  const path = `/ar/invoices?${query.toString()}`

  const invoices = useQuery({
    queryKey: ['ar-invoices', path],
    queryFn: () => apiFetch<{ invoices: ArInvoiceDetail[] }>(path),
  })

  if (invoices.isPending) return <p className="text-sm text-ink-600">Loading invoices…</p>
  if (invoices.error) return <Alert tone="error">{messageFor(invoices.error)}</Alert>

  const rows = invoices.data?.invoices ?? []

  return (
    <div className="overflow-x-auto rounded-md border border-ink-200 bg-white">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr>
            <Th>Invoice</Th>
            <Th>Issued</Th>
            <Th>Due</Th>
            <Th>Aging</Th>
            <Th className="text-right">Billed</Th>
            <Th className="text-right">Outstanding</Th>
            <Th>Payments</Th>
            <Th>Source</Th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <EmptyRow colSpan={8}>
              Nothing outstanding here any more — it has been settled or written off since this page
              loaded.
            </EmptyRow>
          )}

          {rows.map((invoice) => (
            <tr key={invoice.id}>
              <Td className="whitespace-nowrap">
                <span className="block text-ink-900">{invoice.number}</span>
                {invoice.matterDisplayNumber && (
                  <span className="block text-ink-600">{invoice.matterDisplayNumber}</span>
                )}
              </Td>
              <Td className="whitespace-nowrap">{formatDateTime(invoice.issuedAt)}</Td>
              <Td className="whitespace-nowrap">{formatDateTime(invoice.dueAt)}</Td>
              <Td className="whitespace-nowrap">
                <span title={AGING_BUCKET_META[invoice.bucket].description}>
                  {AGING_BUCKET_META[invoice.bucket].label}
                </span>
                {invoice.daysOverdue !== null && invoice.daysOverdue > 0 && (
                  <span className="block tabular-nums text-ink-600">
                    {formatCount(invoice.daysOverdue)} days late
                  </span>
                )}
              </Td>
              <Td className="text-right tabular-nums">{formatMoney(invoice.totalMinor)}</Td>
              <Td className="text-right tabular-nums">{formatMoney(invoice.balanceMinor)}</Td>
              <Td>
                {invoice.payments.length === 0 ? (
                  <span className="text-ink-500">None applied</span>
                ) : (
                  <ul className="space-y-0.5">
                    {invoice.payments.map((payment) => (
                      <li key={payment.id} className="tabular-nums">
                        {formatMoney(payment.amountMinor)}
                        <span className="text-ink-600"> · {formatDateTime(payment.receivedAt)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Td>
              <Td>
                <code className="font-mono text-[11px] text-ink-500">
                  {invoice.sourceSystem}
                  {invoice.sourceId ? ` ${invoice.sourceId}` : ''}
                </code>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * The QuickBooks reconciliation.
 *
 * Its own card, and its own request, so a QBO outage leaves the rest of the
 * screen intact — the AR figures above do not depend on QuickBooks being
 * reachable, and a failed comparison must not look like failed data.
 *
 * `unavailable` is rendered as an explanation rather than an error, because
 * that is what it is. It survived QuickBooks being connected and always will:
 * QBO is read *live* at the moment of comparison, so a rate limit, a 5xx from
 * Intuit or an expired connection each mean the comparison could not be made
 * this time. That is a known state with a stated reason, not something wrong
 * with this page — and it must never render as a variance of zero, which would
 * read as the two systems agreeing.
 */
function Reconciliation() {
  const [period, setPeriod] = useState<{ from: string; to: string }>({ from: '', to: '' })

  const query = new URLSearchParams()
  if (period.from) query.set('from', period.from)
  if (period.to) query.set('to', period.to)

  const result = useQuery({
    queryKey: ['ar-reconciliation', period.from, period.to],
    queryFn: () =>
      apiFetch<{ reconciliation: ArReconciliation }>(`/ar/reconciliation?${query.toString()}`),
  })

  const reconciliation = result.data?.reconciliation ?? null

  return (
    <Card className="mb-6">
      <CardHeader
        title="QuickBooks reconciliation"
        description="This platform's open AR against QuickBooks Online's, over a sample period. Invoices here come from Clio; QuickBooks is the accounting system of record, so the two are independent readings of the same money."
        actions={
          reconciliation && (
            <Badge tone={reconciliation.status === 'reconciled' ? 'positive' : reconciliation.status === 'variance' ? 'critical' : 'neutral'}>
              {reconciliation.status === 'reconciled'
                ? 'Reconciled'
                : reconciliation.status === 'variance'
                  ? 'Variance'
                  : 'Not compared'}
            </Badge>
          )
        }
      />
      <CardBody>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <Field
            id="recon-from"
            label="Invoiced from"
            hint="By invoice date, on both sides. Both dates are included."
          >
            <TextInput
              id="recon-from"
              type="date"
              value={period.from}
              max={period.to || undefined}
              onChange={(event) => setPeriod({ ...period, from: event.target.value })}
            />
          </Field>
          <Field id="recon-to" label="Invoiced to" hint="Leave both blank for everything open.">
            <TextInput
              id="recon-to"
              type="date"
              value={period.to}
              min={period.from || undefined}
              onChange={(event) => setPeriod({ ...period, to: event.target.value })}
            />
          </Field>
        </div>

        {result.isPending && (
          <p className="mt-4 text-sm text-ink-600">Checking against QuickBooks…</p>
        )}
        {result.error && (
          <Alert tone="error" className="mt-4">
            {messageFor(result.error)}
          </Alert>
        )}

        {reconciliation && (
          <>
            <StatRow className="mt-5">
              <Stat
                label="This platform"
                value={formatMoney(reconciliation.ourOutstandingMinor)}
                hint={`${formatCount(reconciliation.ourInvoiceCount)} open ${
                  reconciliation.ourInvoiceCount === 1 ? 'invoice' : 'invoices'
                }, from Clio.`}
              />
              <Stat
                label="QuickBooks"
                value={
                  reconciliation.qboOutstandingMinor === null
                    ? 'Not read'
                    : formatMoney(reconciliation.qboOutstandingMinor)
                }
                tone={reconciliation.qboOutstandingMinor === null ? 'muted' : 'default'}
                hint={
                  reconciliation.qboOutstandingMinor === null
                    ? 'Shown as unread rather than as zero — nobody asked QuickBooks.'
                    : `${formatCount(reconciliation.qboInvoiceCount)} open in QuickBooks.`
                }
              />
              <Stat
                label="Variance"
                value={
                  reconciliation.varianceMinor === null
                    ? '—'
                    : formatMoney(reconciliation.varianceMinor)
                }
                tone={
                  reconciliation.varianceMinor === null
                    ? 'muted'
                    : reconciliation.varianceMinor === 0
                      ? 'default'
                      : 'warning'
                }
                hint="This platform's total minus QuickBooks'. Positive means more AR is reported here."
              />
            </StatRow>

            {reconciliation.status === 'unavailable' && (
              <Alert tone="info" className="mt-4" title="The comparison could not be made">
                {reconciliation.unavailableReason}
              </Alert>
            )}

            {reconciliation.status === 'variance' && (
              <Alert tone="warning" className="mt-4" title="The two systems disagree">
                There is no tolerance on this comparison: both totals are exact minor units read
                from their own system of record, so any difference is a real one — most often an
                invoice posted in one system and not the other.
              </Alert>
            )}

            <p className="mt-3 text-xs text-ink-600">
              Checked {formatRelative(reconciliation.checkedAt)}. QuickBooks is read at the moment
              of comparison rather than synced, so this is two independent readings and not a copy
              checked against itself.
            </p>
          </>
        )}
      </CardBody>
    </Card>
  )
}

/** What a row is, beyond its own name, under each grouping. */
function subtitleFor(line: ArLine, grouping: ArGrouping): string {
  if (grouping === 'client') return line.attorneyName ?? ''
  if (grouping === 'matter') {
    return [line.clientName, line.attorneyName].filter(Boolean).join(' · ')
  }
  return line.clientName ?? ''
}

/**
 * The rule the controls describe.
 *
 * Parsed through the shared money parser rather than `parseFloat`, so a dollar
 * figure never becomes a float on its way to becoming cents: `19.99` through
 * `Number` then `* 100` is `1998.9999...`. A value this cannot parse falls back
 * to the firm default, matching what the API does with the same input — the
 * alternative is a screen that flags a different set of accounts from the one
 * the endpoint would.
 */
function riskRuleFrom(days: string, dollars: string) {
  const parsedDays = Number(days.trim())
  const minDaysOverdue =
    days.trim() !== '' && Number.isFinite(parsedDays)
      ? Math.max(0, Math.trunc(parsedDays))
      : DEFAULT_AR_RISK_RULE.minDaysOverdue

  let minBalanceMinor = DEFAULT_AR_RISK_RULE.minBalanceMinor
  try {
    if (dollars.trim() !== '') minBalanceMinor = Math.max(0, fromDecimalString(dollars))
  } catch {
    // Unparseable, so the default stands. See above.
  }

  return { minDaysOverdue, minBalanceMinor }
}

function isDefaultRule(rule: { minDaysOverdue: number; minBalanceMinor: number }): boolean {
  return (
    rule.minDaysOverdue === DEFAULT_AR_RISK_RULE.minDaysOverdue &&
    rule.minBalanceMinor === DEFAULT_AR_RISK_RULE.minBalanceMinor
  )
}

function messageFor(error: unknown): string {
  return error instanceof ApiError
    ? `${error.message} (${error.status})`
    : 'Something went wrong. Try again.'
}

/**
 * Downloads the lines currently on screen.
 *
 * The **same query string** the table was built from, which is what makes the
 * file and the screen the same figures — the criterion KAN-88 sets. A button
 * that assembled its own parameters is how an export comes to disagree with the
 * page it was taken from, and the disagreement is always found by whoever is
 * presenting the spreadsheet.
 */
function ExportButton({ query }: { query: URLSearchParams }) {
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function download() {
    setError(null)
    setBusy(true)
    try {
      await apiDownload(
        `/ar/export?${query.toString()}`,
        `accounts-receivable-${new Date().toISOString().slice(0, 10)}.csv`
      )
    } catch (downloadError) {
      setError(messageFor(downloadError))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex items-center gap-2">
      {error && <span className="text-xs text-red-700">{error}</span>}
      <Button size="sm" onClick={download} disabled={busy}>
        {busy ? 'Preparing…' : 'Export CSV'}
      </Button>
    </div>
  )
}

interface CommitmentRow {
  id: string
  clientId: string
  clientName: string
  matterLabel: string | null
  invoiceNumber: string | null
  amountMinor: number
  promisedOn: string
  status: PaymentCommitmentStatus
  note: string | null
  recordedByName: string | null
  isOverdue: boolean
}

/**
 * What clients have said they will pay (KAN-88).
 *
 * Its own card below the aging table rather than a column inside it, because
 * these are a different kind of fact: the table is derived from invoices and
 * this is a record of conversations. Keeping them visibly apart is what stops a
 * promise being read as a receivable.
 *
 * Closing one is a person's judgement and there is no automatic path: a client
 * who pays half, or a week late, or against a different invoice has done
 * something the ledger cannot classify.
 */
function Commitments({ canManage }: { canManage: boolean }) {
  const queryClient = useQueryClient()
  const [showClosed, setShowClosed] = useState(false)

  const commitments = useQuery({
    queryKey: ['ar-commitments', showClosed],
    queryFn: () =>
      apiFetch<CommitmentRow[]>(`/ar/commitments?includeClosed=${showClosed ? 'true' : 'false'}`),
  })

  const close = useMutation({
    mutationFn: ({ id, status }: { id: string; status: PaymentCommitmentStatus }) =>
      apiFetch(`/ar/commitments/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ar-commitments'] }),
  })

  const rows = commitments.data ?? []
  const overdue = rows.filter((row) => row.isOverdue).length

  return (
    <Card className="mt-6">
      <CardHeader
        title="Payment commitments"
        description="What a client said they would pay, and by when. Never netted against the balance — a promise is evidence about a conversation, not about money."
        actions={
          <div className="flex items-center gap-2">
            {overdue > 0 && <Badge tone="warning">{overdue} past due</Badge>}
            <Button size="sm" variant="ghost" onClick={() => setShowClosed((open) => !open)}>
              {showClosed ? 'Open only' : 'Include closed'}
            </Button>
          </div>
        }
      />

      <TableWrap>
        <thead>
          <tr>
            <Th>Client</Th>
            <Th>Against</Th>
            <Th className="text-right">Amount</Th>
            <Th>Promised</Th>
            <Th>Recorded by</Th>
            <Th>Status</Th>
            {canManage && <Th />}
          </tr>
        </thead>
        <tbody>
          {commitments.isPending && <EmptyRow colSpan={canManage ? 7 : 6}>Loading…</EmptyRow>}
          {!commitments.isPending && rows.length === 0 && (
            <EmptyRow colSpan={canManage ? 7 : 6}>
              Nothing recorded. A commitment exists only because somebody typed it — nothing here
              is inferred from a payment.
            </EmptyRow>
          )}
          {rows.map((row) => (
            <tr key={row.id} className={row.isOverdue ? 'bg-accent-50/60' : undefined}>
              <Td>{row.clientName}</Td>
              <Td>
                {row.invoiceNumber
                  ? `Invoice ${row.invoiceNumber}`
                  : (row.matterLabel ?? 'The outstanding balance')}
                {row.note && <p className="mt-0.5 text-xs text-ink-500">{row.note}</p>}
              </Td>
              <Td className="text-right tabular-nums">{formatMoney(row.amountMinor)}</Td>
              <Td>
                {formatDate(row.promisedOn)}
                {row.isOverdue && (
                  <span className="ml-2 text-xs text-accent-700">past due</span>
                )}
              </Td>
              <Td className="text-xs text-ink-600">{row.recordedByName ?? '—'}</Td>
              <Td>
                <Badge tone={row.status === 'open' ? 'brand' : 'neutral'}>
                  {PAYMENT_COMMITMENT_STATUS_LABELS[row.status]}
                </Badge>
              </Td>
              {canManage && (
                <Td>
                  {row.status === 'open' && (
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => close.mutate({ id: row.id, status: 'kept' })}
                      >
                        Kept
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => close.mutate({ id: row.id, status: 'missed' })}
                      >
                        Missed
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => close.mutate({ id: row.id, status: 'withdrawn' })}
                      >
                        Withdrawn
                      </Button>
                    </div>
                  )}
                </Td>
              )}
            </tr>
          ))}
        </tbody>
      </TableWrap>
    </Card>
  )
}
